import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import { P1_KEY } from './use-p1';

import type { P1Category, P1EscalationLevel, P1IncidentRaw } from '../types';
import type { UseMutationResult } from '@tanstack/react-query';

// Mutations da Tela 8 (lifecycle + escalate + trigger-runbook). P1Incident não tem
// version → sem If-Match. Cada sucesso invalida a lista + volume-status (P1_KEY).
function useInvalidateP1(): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: P1_KEY });
}

// D1 — "Acompanhar" do card mapeia para acknowledge (marca ciente/assumido).
export function useAcknowledgeP1(): UseMutationResult<P1IncidentRaw, Error, string> {
  const invalidate = useInvalidateP1();
  return useMutation({ mutationFn: (id: string) => api.acknowledgeP1(id), onSuccess: invalidate });
}

export function useHandleP1(): UseMutationResult<P1IncidentRaw, Error, string> {
  const invalidate = useInvalidateP1();
  return useMutation({ mutationFn: (id: string) => api.handleP1(id), onSuccess: invalidate });
}

export function useResolveP1(): UseMutationResult<
  P1IncidentRaw,
  Error,
  { id: string; resolution: string }
> {
  const invalidate = useInvalidateP1();
  return useMutation({
    mutationFn: ({ id, resolution }) => api.resolveP1(id, resolution),
    onSuccess: invalidate,
  });
}

export function useCloseP1(): UseMutationResult<P1IncidentRaw, Error, string> {
  const invalidate = useInvalidateP1();
  return useMutation({ mutationFn: (id: string) => api.closeP1(id), onSuccess: invalidate });
}

export function useEscalateP1(): UseMutationResult<
  P1IncidentRaw,
  Error,
  { id: string; toLevel: P1EscalationLevel; justification: string }
> {
  const invalidate = useInvalidateP1();
  return useMutation({
    mutationFn: ({ id, toLevel, justification }) => api.escalateP1(id, { toLevel, justification }),
    onSuccess: invalidate,
  });
}

// D3 — runbook por categoria (slug resolvido na UI). 202 { executionId, status }.
export function useTriggerP1Runbook(): UseMutationResult<
  { executionId: string; status: string },
  Error,
  { id: string; runbookSlug: string }
> {
  const invalidate = useInvalidateP1();
  return useMutation({
    mutationFn: ({ id, runbookSlug }) => api.triggerP1Runbook(id, runbookSlug),
    onSuccess: invalidate,
  });
}

// T8-3 — flag manual (p1-manual-flag, Pro+ opt-in OFF). 201 P1Incident.
export function useManualFlagP1(): UseMutationResult<
  P1IncidentRaw,
  Error,
  { conversationId: string; category: P1Category; reason: string }
> {
  const invalidate = useInvalidateP1();
  return useMutation({
    mutationFn: (body) => api.manualFlagP1(body),
    onSuccess: invalidate,
  });
}
